import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { namespace } from '@common-config/constants';
import { backendApi } from '@common/reducers/backend-api/backend-api';
import { skipToken } from '@reduxjs/toolkit/query';
import { FilePlus2, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useConfirmDialog } from '@common/components/use-confirm-dialog';
import { MarkdownEditorModal } from './MarkdownEditorModal';
import { muted, text } from './agent-inspect-shared';
import { reportAgentEvent } from '@common/services/agent-analytics';

/**
 * A small VSCode-ish knowledge file manager: a dropzone to add files, a list of
 * the agent's knowledge files, click a file to open it in the Markdown editor
 * (edit + preview), save changes back, create a new file, or delete one. Wired
 * to the Bridge's whole-file knowledge base (the Claude Code session Reads these
 * on demand).
 *
 * A NEW agent (no `agentId` yet) gets the same manager over a LOCAL list the form
 * owns (`pending` / `onPendingChange`) — like attaching files in a chat before Send:
 * nothing is uploaded until the agent is saved, then AgentForm uploads them
 * (`uploadPendingKnowledgeFiles`). With an `agentId` it works on the server as before.
 */
export interface PendingKnowledgeFile {
  name: string;
  contentBase64: string;
  bytes: number;
}

export interface KnowledgeFilesManagerProps {
  agentId?: string;
  /** New agent only: the files waiting for the first save. */
  pending?: PendingKnowledgeFile[];
  onPendingChange?: (files: PendingKnowledgeFile[]) => void;
  isRtl?: boolean;
}

/** Read a File as base64 (no data: prefix) for the JSON upload path. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
/** UTF-8 safe base64 for text the user typed in the editor. */
const toBase64Text = (value: string): string => btoa(unescape(encodeURIComponent(value)));
/** Its inverse — open a pending file in the editor. */
const fromBase64Text = (b64: string): string => decodeURIComponent(escape(atob(b64)));
/** Byte length of base64 content. */
const base64Bytes = (b64: string): number => Math.floor((b64.length * 3) / 4) - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
const fmtBytes = (n?: number) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const KnowledgeFilesManager: React.FC<KnowledgeFilesManagerProps> = ({ agentId, pending = [], onPendingChange, isRtl }) => {
  const { t } = useTranslation(namespace);
  const { confirm, dialog } = useConfirmDialog();
  const { data, isLoading } = backendApi.useGetMyBridgeAgentKnowledgeFilesQuery(agentId ? { agentId } : skipToken);
  const [uploadFile, { isLoading: uploading }] = backendApi.useUploadMyBridgeAgentKnowledgeFileMutation();
  const [deleteFile] = backendApi.useDeleteMyBridgeAgentKnowledgeFileMutation();
  const [fetchContent] = backendApi.useLazyGetMyBridgeAgentKnowledgeFileContentQuery();

  const [editing, setEditing] = useState<{ name: string; content: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const files: { name: string; bytes?: number; size?: number }[] = agentId ? (data?.files ?? []) : pending;
  const errorOf = (e: any, key: string, name: string) => e?.data?.message || t(`myBridge.kb.${key}`, { name });

  /** Put a file: on the server (existing agent) or into the pending list (same name replaces). */
  const put = async (name: string, contentBase64: string) => {
    if (agentId) {
      await uploadFile({ agentId, name, contentBase64 }).unwrap();
      return;
    }
    onPendingChange?.([...pending.filter((p) => p.name !== name), { name, contentBase64, bytes: base64Bytes(contentBase64) }]);
  };

  const onDrop = async (accepted: File[]) => {
    setError(null);
    for (const f of accepted) {
      try {
        await put(f.name, await fileToBase64(f));
        if (agentId) reportAgentEvent('agent_knowledge_uploaded', 'claude', { agentId });
      } catch (e: any) {
        setError(errorOf(e, 'uploadFailed', f.name));
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true, // we trigger it from the explicit button
  });

  const openFile = async (name: string) => {
    setError(null);
    try {
      if (!agentId) {
        const p = pending.find((x) => x.name === name);
        if (p) setEditing({ name: p.name, content: fromBase64Text(p.contentBase64) });
        return;
      }
      const res = await fetchContent({ agentId, name }).unwrap();
      setEditing({ name: res.name, content: res.content });
    } catch (e: any) {
      setError(errorOf(e, 'openFailed', name));
    }
  };

  const newFile = () => {
    const name = window.prompt(t('myBridge.kb.newFilePrompt'), 'notes.md')?.trim();
    if (name) setEditing({ name, content: '' });
  };

  const saveEditing = async (content: string) => {
    if (!editing) return;
    try {
      await put(editing.name, toBase64Text(content));
    } catch (e: any) {
      setError(errorOf(e, 'saveFailed', editing.name));
    }
  };

  const removeFile = async (name: string) => {
    if (!(await confirm({ content: t('myBridge.kb.deleteConfirm', { name }) }))) return;
    setError(null);
    try {
      if (!agentId) {
        onPendingChange?.(pending.filter((p) => p.name !== name));
        return;
      }
      await deleteFile({ agentId, name }).unwrap();
    } catch (e: any) {
      setError(errorOf(e, 'deleteFailed', name));
    }
  };

  return (
    <div className="space-y-2">
      {dialog}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium" style={text}>{t('myBridge.kb.title')}</span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={newFile}>
            <FilePlus2 className="h-3.5 w-3.5" /><span className="ms-1.5">{t('myBridge.kb.newFile')}</span>
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={open} disabled={uploading}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            <span className="ms-1.5">{t('myBridge.kb.upload')}</span>
          </Button>
        </div>
      </div>

      {/* Dropzone — `relative`: react-dropzone's hidden input is absolutely
          positioned; without a positioned ancestor it escaped the form's scroll
          pane and made the whole page scroll. */}
      <div
        {...getRootProps()}
        className="relative rounded-md border border-dashed p-4 text-center text-sm cursor-default"
        style={{
          borderColor: isDragActive ? 'var(--cta-button-bg)' : 'var(--color-border)',
          background: isDragActive ? 'var(--color-bg-subtle, rgba(0,0,0,0.03))' : 'transparent',
          color: 'var(--color-text-muted)',
        }}
      >
        <input {...getInputProps()} />
        {isDragActive ? t('myBridge.kb.dropHere') : t('myBridge.kb.dropHint')}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* File list */}
      {agentId && isLoading ? (
        <div className="py-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin" style={muted} /></div>
      ) : files.length === 0 ? (
        <p className="text-xs" style={muted}>{t('myBridge.kb.empty')}</p>
      ) : (
        <div className="rounded-md border divide-y" style={{ borderColor: 'var(--color-border)' }}>
          {files.map((f) => (
            <div key={f.name} className="flex items-center gap-2 px-3 py-2" dir="auto">
              <FileText className="h-4 w-4 flex-none" style={muted} />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => void openFile(f.name)}
                    className="flex-1 min-w-0 text-start text-sm truncate hover:underline"
                    style={text}
                  >
                    {f.name}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('myBridge.kb.openInEditor')}</TooltipContent>
              </Tooltip>
              <span className="text-xs flex-none" style={muted}>{fmtBytes(f.bytes ?? f.size)}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => void removeFile(f.name)}
                    className="flex-none p-1"
                    style={{ color: '#dc2626' }}
                    aria-label={t('myBridge.kb.deleteFile')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('myBridge.kb.deleteFile')}</TooltipContent>
              </Tooltip>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <MarkdownEditorModal
          open={!!editing}
          onOpenChange={(o) => { if (!o) setEditing(null); }}
          title={t('myBridge.kb.editTitle', { name: editing.name })}
          value={editing.content}
          onSave={(c) => void saveEditing(c)}
          isRtl={isRtl}
        />
      )}
    </div>
  );
};

/**
 * A new agent's first save: upload its pending knowledge files with the id it just
 * got. Never throws — answers the names that failed (the agent itself is saved).
 */
export async function uploadPendingKnowledgeFiles(
  upload: (arg: { agentId: string; name: string; contentBase64: string }) => { unwrap(): Promise<unknown> },
  agentId: string,
  files: PendingKnowledgeFile[],
): Promise<string[]> {
  const failed: string[] = [];
  for (const f of files) {
    try {
      await upload({ agentId, name: f.name, contentBase64: f.contentBase64 }).unwrap();
      reportAgentEvent('agent_knowledge_uploaded', 'claude', { agentId });
    } catch {
      failed.push(f.name);
    }
  }
  return failed;
}
